import { Set } from 'immutable';
import { createSelector } from 'reselect';
import { getProduct, getName, getDistress, getFinish, getCustom, getCustomValue, getOrderedFields, getOrderedFieldsArray, getFinishOptions, getCustomOptions, selectedFieldOption, getWoodThumbnail, getDistressThumbnail, getDefaultProps, getDefaultPropsByFieldName, addPrefixForValidFieldValue, addSuffixForValidFieldValue } from './index';
import * as fieldName from '../constants/Fields';
import * as woodBeamFieldName from '../constants/WoodBeamFields';

const getQuantityHeading = (state) => state.getIn(['fields', woodBeamFieldName.QUANTITY_HEADING]);
const getMeasurementA = (state) => state.getIn(['fields', woodBeamFieldName.MEASUREMENT_A]);
const getMeasurementB = (state) => state.getIn(['fields', woodBeamFieldName.MEASUREMENT_B]);
const getMeasurementC = (state) => state.getIn(['fields', woodBeamFieldName.MEASUREMENT_C]);
const getMeasurementD = (state) => state.getIn(['fields', woodBeamFieldName.MEASUREMENT_D]);
const getMeasurementE = (state) => state.getIn(['fields', woodBeamFieldName.MEASUREMENT_E]);
const getLengths = (state) => state.getIn(['fields', woodBeamFieldName.LENGTHS]);
const getTotalFeet = (state) => state.getIn(['fields', woodBeamFieldName.TOTAL_FEET]);

const getDisplayMeasurementA = (state) => state.getIn(['product', woodBeamFieldName.DISPLAY_A]);
const getDisplayMeasurementB = (state) => state.getIn(['product', woodBeamFieldName.DISPLAY_B]);
const getDisplayMeasurementC = (state) => state.getIn(['product', woodBeamFieldName.DISPLAY_C]);
const getDisplayMeasurementD = (state) => state.getIn(['product', woodBeamFieldName.DISPLAY_D]);
const getDisplayMeasurementE = (state) => state.getIn(['product', woodBeamFieldName.DISPLAY_E]);

const createMeasurementOptions = (valueFunctionsArray, prefix) => {
  return createSelector(
    valueFunctionsArray,
    (measurement, display) => {
      let calculatedProps = {};

      if (!display) {
        calculatedProps.hide = true;
        calculatedProps.summaryText = null;
      } else {
        calculatedProps.summaryText = addPrefixForValidFieldValue(measurement, prefix);
      }

      return Object.assign({}, getDefaultProps(measurement), calculatedProps);
    }
  );
}

const getMeasurementAOptions = createMeasurementOptions([getMeasurementA, getDisplayMeasurementA], 'Measurement A: ');
const getMeasurementBOptions = createMeasurementOptions([getMeasurementB, getDisplayMeasurementB], 'Measurement B: ');
const getMeasurementCOptions = createMeasurementOptions([getMeasurementC, getDisplayMeasurementC], 'Measurement C: ');
const getMeasurementDOptions = createMeasurementOptions([getMeasurementD, getDisplayMeasurementD], 'Measurement D: ');
const getMeasurementEOptions = createMeasurementOptions([getMeasurementE, getDisplayMeasurementE], 'Measurement E: ');

const getQuantityHeadingOptions = createSelector(
  [getQuantityHeading],
  (heading) => {
    return Object.assign({}, getDefaultProps(heading));
  }
);

const getLengthsOptions = createSelector(
  [getLengths],
  (lengths) => {
    const lengthsValue = lengths.get('value');
    
    const calculatedProps = {
      summaryText: lengthsValue ? lengthsValue.replace(/^\s+|\s+$/, '').replace(/\n+/g, ', ') : null
    };
    
    return Object.assign({}, getDefaultProps(lengths), calculatedProps);
  }
)

const getTotalFeetOptions = createSelector(
  [getTotalFeet],
  (totalFeet) => {
    const calculatedProps = {
      note: totalFeet.get('note'),
      summaryText: addSuffixForValidFieldValue(totalFeet, ' total linear feet')
    };
    
    return Object.assign({}, getDefaultProps(totalFeet), calculatedProps);
  }
);


export const getWoodBeamFieldProps = (state, name) => {
  switch (name) {
    case woodBeamFieldName.MEASUREMENT_A:
      return getMeasurementAOptions(state);
    case woodBeamFieldName.MEASUREMENT_B:
      return getMeasurementBOptions(state);
    case woodBeamFieldName.MEASUREMENT_C:
      return getMeasurementCOptions(state);
    case woodBeamFieldName.MEASUREMENT_D:
      return getMeasurementDOptions(state);
    case woodBeamFieldName.MEASUREMENT_E:
      return getMeasurementEOptions(state);
    case woodBeamFieldName.QUANTITY_HEADING:
      return getQuantityHeadingOptions(state);
    case woodBeamFieldName.LENGTHS:
      return getLengthsOptions(state);
    case woodBeamFieldName.TOTAL_FEET:
      return getTotalFeetOptions(state);
    case fieldName.FINISH:
      return getFinishOptions(state);
    case fieldName.CUSTOM:
      return getCustomOptions(state);
  }

  return getDefaultPropsByFieldName(state, name);
};

export const getWoodBeamSelectedOption = (state, name) => {
  switch(name) {
    case fieldName.WOOD:
      return getWoodThumbnail(state);
    case fieldName.DISTRESS:
      return getDistressThumbnail(state);
  }
};

export const getIllustration = createSelector(
  [getProduct],
  (product) => product.get('image')
);

export const getAltText = createSelector(
  [getProduct],
  (product) => product.get('imageAlt')
);

export const getWoodBeamSummaryFieldValues = (state) => {
  return getOrderedFieldsArray(state).map((name) => {
    const props = getWoodBeamFieldProps(state, name);
    return (props.hide || props.disabled.indexOf(props.selected) >= 0) ? false : props.summaryText;
  }).filter((displayValue) => displayValue ? true : false);
};

export const getWoodBeamOptionThumbnails = (state) => {
  const thumbnailFields = [fieldName.WOOD, fieldName.DISTRESS];
  return thumbnailFields.map((name) => {
    const fieldProps = getWoodBeamFieldProps(state, name),
          disabledOptions = fieldProps.disabled,
          isDisabled = disabledOptions && disabledOptions.indexOf(fieldProps.selected) > -1,
          isHidden = fieldProps.hide === true;
    
    return (isDisabled || isHidden) ? {} : getWoodBeamSelectedOption(state, name);
  }).filter(thumbnail => thumbnail.image ? true : false);
};
